/**
 * The browser sessions signed in to this account (`docs/AuthSessions.md` §7).
 *
 * Scoped to the caller by the server — there is no admin variant, because a list of
 * someone else's live credentials is not an administrative convenience worth the blast
 * radius. Every query here is therefore unparameterised.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

const backendApi = config.backendApi;

export interface Session {
  id: string;
  createdAt: string;
  /** Last request on this session, to a five-minute resolution — the server throttles the write. */
  lastSeenAt: string;
  expiresAt: string;
  /** Best-effort: a proxy may withhold it. */
  sourceIp: string | null;
  userAgent: string | null;
  /** The session making this request. The one the UI must not let you revoke by accident. */
  isCurrent: boolean;
  /** Running on the longer installed-app window rather than the ordinary one. */
  extended: boolean;
}

interface SessionListResponse {
  sessions: Session[];
}

export const SESSIONS_QUERY_KEY = ['auth-sessions'] as const;

export function useSessions() {
  return useQuery<Session[]>({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: async () => {
      const response = await axios.get<SessionListResponse>(`${backendApi}/api/auth/sessions/`, {
        withCredentials: true,
      });
      // Defensive: hand callers an array rather than letting an unexpected shape reach a
      // `.map` in the component.
      return Array.isArray(response.data.sessions) ? response.data.sessions : [];
    },
  });
}

/**
 * Sign one session out.
 *
 * Revoking your own is allowed by the API — it is logout by another name — but the
 * response carries no cookie change, so a caller that revokes its own session must send
 * the user to sign in again rather than leave them on a page whose next request will 401.
 */
export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${backendApi}/api/auth/sessions/${id}`, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });
    },
  });
}
